'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      question: 'What are the requirements to join the AI Change Leaders network?',
      answer: 'We seek senior consultants and boutique firms with 5+ years of experience in transformation, change management, or related fields. Ideal partners have a track record of enterprise client work, strong communication skills, and genuine interest in AI adoption leadership. Technical AI expertise is valuable but not required—we provide methodology training.'
    },
    {
      question: 'How does the revenue sharing model work?',
      answer: 'Partners receive competitive revenue shares on client engagements they deliver, typically ranging from 60-75% depending on project scope and partner involvement. We handle client acquisition, contracting, and administrative overhead, allowing you to focus on delivery excellence.'
    },
    {
      question: 'What is the time commitment for network partners?',
      answer: 'Commitment varies based on your availability and project pipeline. Some partners work full-time on AI Change Leaders engagements, while others balance network projects with independent work. We respect your autonomy and work together to match opportunities with your capacity.'
    },
    {
      question: 'Do I need to be based in the UK to join?',
      answer: 'While our primary market is the UK, we welcome partners from other regions who can serve UK-based enterprise clients. Remote delivery is common for many engagements, though some projects may require on-site presence in client locations.'
    },
    {
      question: 'What training and support do partners receive?',
      answer: 'All partners complete our AI Change Leadership certification program covering our methodology frameworks, tools, and delivery approaches. Ongoing support includes quarterly partner summits, private community platform access, methodology updates, and collaborative learning opportunities with fellow partners.'
    },
    {
      question: 'How long does the application process take?',
      answer: 'From application submission to active partnership typically takes 4-6 weeks. This includes application review (5 business days), discovery interview, methodology training (2-3 weeks), and network onboarding. We maintain high standards to ensure network quality and partner success.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!isHydrated) {
    return (
      <section className="py-20 lg:py-32 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-3/4 mx-auto"></div>
              <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
              <div className="space-y-3 mt-8">
                <div className="h-16 bg-muted rounded"></div>
                <div className="h-16 bg-muted rounded"></div>
                <div className="h-16 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-32 bg-card">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-bold font-headline text-primary mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-muted-foreground font-body">
              Common questions about joining the AI Change Leaders partner network.
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-background rounded-xl border border-border overflow-hidden shadow-card hover:shadow-modal transition-all duration-300"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted/50 transition-colors duration-300"
                >
                  <span className="font-bold font-headline text-primary pr-4">
                    {faq.question}
                  </span>
                  <Icon
                    name="ChevronDownIcon"
                    size={24}
                    variant="outline"
                    className={`text-secondary flex-shrink-0 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-5 text-foreground font-body leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 bg-primary/5 rounded-xl p-8 border border-primary/20 text-center">
            <h3 className="text-xl font-bold font-headline text-primary mb-3">
              Still Have Questions?
            </h3>
            <p className="text-foreground font-body mb-6">
              Our partnership team is here to help. Schedule a call to discuss your specific situation and learn more about network opportunities.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-secondary-foreground rounded-lg font-semibold font-cta hover:bg-secondary/90 transition-all duration-300 shadow-subtle hover:shadow-strong"
            >
              Contact Partnership Team
              <Icon name="ArrowRightIcon" size={20} variant="solid" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;